import { SnackBarService } from "@teammood/shared/ui/snack-bar";
import { Timezone, TimeZoneService } from '@teammood/shared/util/locale';
import { debounceTime, distinctUntilChanged, filter, firstValueFrom, merge, tap } from 'rxjs';
import { AccountService } from './account.service';
import { TeamMemberForAccount, Unsubscribable } from "./account.type";
import { Component, OnInit, ChangeDetectionStrategy } from "@angular/core";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule, Validators } from "@angular/forms";
import { TmValidators } from "@teammood/shared/util/form";
import { MatFormFieldModule } from "@angular/material/form-field";
import { MatSelectModule } from "@angular/material/select";
import { MatTooltipModule } from "@angular/material/tooltip";
import { TmLanguageSelectComponent } from "@teammood/shared/ui/language-select";
import { TmOutOfOfficeComponent } from "@teammood/shared/ui/out-of-office";
import { TmTimepickerComponent } from "@teammood/shared/ui/timepicker";
import { TmTimeZonePickerComponent } from "@teammood/shared/ui/timezone-picker";
import { CommonModule } from "@angular/common";
import { isEqual } from "lodash";
import {
    NotificationDaysSettingsComponent
} from "../notification-days/notification-days-settings/notification-days-settings.component";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { TmSlideToggleComponent } from "@teammood/shared/ui/slide-toggle";


@Component({
    selector: "account-setup",
    templateUrl: "./account-setup.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        CommonModule,
        FormsModule,
        MatFormFieldModule,
        MatSelectModule,
        MatTooltipModule,
        ReactiveFormsModule,
        TmLanguageSelectComponent,
        TmOutOfOfficeComponent,
        TmTimepickerComponent,
        TmTimeZonePickerComponent,
        TranslateModule,
        NotificationDaysSettingsComponent,
        TmSlideToggleComponent
    ]
})
export class AccountSetupComponent implements OnInit {
    
    eq: (a: any, b: any) => boolean;
    teamMember: TeamMemberForAccount;
    errors: any;
    languages: string[];
    timezones: Timezone[];
    passwordRest: boolean;

    notificationTypes: Unsubscribable[] = [];

    form: FormGroup = new FormGroup({
        firstName: new FormControl('', [TmValidators.required, Validators.minLength(2)]),
        lastName: new FormControl('', [TmValidators.required, Validators.minLength(2)]),
        notificationTime: new FormGroup({
          timeZone: new FormControl(''),
          time: new FormControl(''),
        }),
        language: new FormControl(''),
        outOfOffice: new FormControl(),
      });

    get firstName() {
        return this.form.get('firstName');
    }

    get lastName() {
        return this.form.get('lastName');
    }

    constructor(
        private translate: TranslateService,
        private timeZoneService: TimeZoneService,
        private accountService: AccountService,
        private snackBarService: SnackBarService,
    ) {
        
        const controls = Object.keys(this.form.controls)
            .map(key => this.form.get(key));
        merge(
        ...controls.map(control => control.valueChanges.pipe(debounceTime(500), distinctUntilChanged()))
        ).pipe(
        filter(() => this.form.valid),
        tap(() => this.saveChanges()),
        takeUntilDestroyed()
        ).subscribe()
    }

    async ngOnInit() {
        this.eq = isEqual; // used in template

        this.timezones = await firstValueFrom(this.timeZoneService.query());
           
        this.accountService.get().then((teamMember) => {
            this.teamMember = teamMember;
            this.form.reset(teamMember, { emitEvent: false });
        });

        this.accountService.getNotificationTypes().pipe(
            tap(notificationTypes => {
                this.notificationTypes = notificationTypes;
            })
        ).subscribe();
    }

    onTeamChange (teamMember: TeamMemberForAccount) {
        this.teamMember = teamMember;
    }

    saveChanges () {
        const formValue = this.form.getRawValue();
        const updatedMember = { ...this.teamMember, ...formValue };
        this.accountService.update(updatedMember).then(() => {
            this.snackBarService.success(this.translate.instant('saved'));
        },
        (e) => {
            this.errors = {};
            if (e.status === 400 && e.error.errors) {
                
                this.errors = Object.keys(e.error.errors).reduce((prev, curr)=>{
                    const key = curr.replace('obj.', '');
                    prev[key] = e.error.errors[curr];
                    return prev
                }, {});
            } else {
                this.snackBarService.error(this.translate.instant('error'));
            }
        });
    }

    resetPassword () {
        this.accountService.resetPassword(this.teamMember.email.value).then(() => {
            this.passwordRest = true;
        }, () => {
            this.snackBarService.error(this.translate.instant('error'));
        });
    }

    protected toggle_notification(notificationType: Unsubscribable) {
        this.accountService.toggleNotificationTypes(notificationType).pipe(

        ).subscribe();
    }
}